import * as React from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Checkbox,
  IconButton,
  Button,
  Paper,
  Collapse,
  Skeleton,
  TablePagination,
  Divider,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import DI from "../../utility/DI";
import { toTitleCase } from "../../utility/tools";
import Report from "../../reports/Reports";
import { AppContext } from "../../../App";
import ConfirmDialog from "../../utility/ConfirmDialog";

/**
 * MobileViewOrders — Modern, compact card-based transaction list for Shared Expenses.
 * Collapsed by default for maximum screen efficiency (shows 5-6 cards at once).
 * Expands on tap to reveal participant breakdown and settlement actions.
 */
const MobileViewOrders = (props) => {
  const [open, setOpen] = React.useState(false);
  const [confirmDialog, setConfirmDialog] = React.useState({
    open: false,
    title: "",
    content: "",
    confirmText: "Confirm",
    confirmColor: "primary",
    onConfirm: () => { },
  });

  const {
    state: { mode },
  } = React.useContext(AppContext);
  const isDark = mode === "dark";

  const {
    deleteTransactions,
    transactionType,
    changestatus,
    makePaid,
    di: { session, error },
  } = props;

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [selected, setSelected] = React.useState([]);
  const [expandedMap, setExpandedMap] = React.useState({});

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  React.useEffect(() => {
    setPage(0);
  }, [props.transaction]);

  const visibleRows = React.useMemo(
    () =>
      (props.transaction || []).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [page, rowsPerPage, props.transaction]
  );

  const isSelected = (id) => selected.includes(id);

  const handleClick = (e, id) => {
    e.stopPropagation();
    setSelected((prev) => {
      const index = prev.indexOf(id);
      if (index !== -1) {
        return prev.filter((item_id) => item_id !== id);
      }
      return [...prev, id];
    });
  };

  const toggleExpand = (id) => {
    setExpandedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const isAllExpanded =
    visibleRows.length > 0 &&
    visibleRows.every((row) => !!expandedMap[row.id]);

  const handleToggleAll = () => {
    if (isAllExpanded) {
      setExpandedMap({});
    } else {
      const newMap = { ...expandedMap };
      visibleRows.forEach((row) => {
        newMap[row.id] = true;
      });
      setExpandedMap(newMap);
    }
  };

  const selectedAll =
    props.transaction?.length > 0 &&
    selected.length === props.transaction?.length;

  return (
    <Box sx={{ width: "100%" }}>
      {/* ===== HEADER TOOLBAR ===== */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: 1.2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
          <Checkbox
            size="small"
            color="primary"
            checked={selectedAll}
            indeterminate={selected.length > 0 && !selectedAll}
            onClick={() => {
              if (selectedAll) setSelected([]);
              else setSelected(props.transaction?.map((item) => item.id) || []);
            }}
            sx={{ p: 0.4 }}
          />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: "text.primary" }}>
            {props.title || "Expenses"}
          </Typography>
          {props.transaction?.length > 0 && (
            <Chip
              size="small"
              label={props.transaction.length}
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
              }}
            />
          )}
          {visibleRows.length > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={handleToggleAll}
              startIcon={
                isAllExpanded ? (
                  <UnfoldLessIcon sx={{ fontSize: "14px !important" }} />
                ) : (
                  <UnfoldMoreIcon sx={{ fontSize: "14px !important" }} />
                )
              }
              sx={{
                textTransform: "none",
                fontSize: 11,
                fontWeight: 600,
                color: "primary.main",
                py: 0.2,
                px: 0.8,
                minWidth: 0,
                borderRadius: 1.5,
                ml: 0.5,
                bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                "&:hover": {
                  bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                },
              }}
            >
              {isAllExpanded ? "Collapse All" : "Open All"}
            </Button>
          )}
        </Box>

        {selected.length > 0 && (
          <Button
            variant="contained"
            size="small"
            onClick={() => setOpen(true)}
            startIcon={<QueryStatsIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              py: 0.4,
              px: 1.5,
            }}
          >
            Report ({selected.length})
          </Button>
        )}
      </Box>

      {/* ===== TRANSACTIONS LIST ===== */}
      {props.loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[1, 2, 3, 4].map((n) => (
            <Skeleton
              key={n}
              variant="rounded"
              height={64}
              sx={{ borderRadius: 2.5 }}
            />
          ))}
        </Box>
      ) : visibleRows.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 3,
            border: "1px dashed",
            borderColor: "divider",
            bgcolor: isDark ? "rgba(30, 41, 59, 0.3)" : "rgba(0, 0, 0, 0.01)",
          }}
        >
          <ReceiptLongIcon sx={{ fontSize: 48, color: "text.secondary", opacity: 0.35, mb: 1 }} />
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            No transactions found
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Expenses added to this group will show up here
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {visibleRows.map((row) => {
            const itemSelected = isSelected(row.id);
            const isPayer = String(row.user_id) === String(session?.id);
            const userCollab = row.collaborators?.find(
              (c) => String(c.id) === String(session?.id)
            );
            const isCollaborator = !!userCollab;
            const totalAmount = parseFloat(row.amount) || 0;
            const isPaid = row.paid === "true";
            const isExpanded = !!expandedMap[row.id];
            // Compute real per-person share from collaborator count — more reliable than DB each_share
            // (old records have each_share=1 due to a backend bug now fixed)
            const collabCount = row.collaborators?.length || 1;
            const computedEachShare = totalAmount > 0 ? totalAmount / collabCount : 0;

            // Determine Payer Name
            let payerName = "Someone";
            if (isPayer) {
              payerName = "You";
            } else {
              const payerCollab = row.collaborators?.find(
                (c) => String(c.id) === String(row.user_id)
              );
              payerName = payerCollab ? toTitleCase(payerCollab.name) : "Someone";
            }

            // Determine user's financial involvement
            let yourImpactType = "neutral";
            let yourImpactAmount = 0;

            if (isPayer) {
              // Use computed share (amount ÷ participants) — avoids stale each_share=1 bug in old records
              const userShare = computedEachShare;
              yourImpactAmount = totalAmount - userShare;
              yourImpactType = yourImpactAmount > 0 ? "lent" : "personal";
            } else if (isCollaborator) {
              yourImpactAmount = parseFloat(userCollab?.shared_amount) || 0;
              yourImpactType = "borrowed";
            }

            const hasUserAcknowledged = userCollab?.approved === "true";
            const allAcknowledged = row.collaborators?.every(
              (c) => c.approved === "true"
            );

            // Clean formatted date: "15 May 26" (DD Mon YY)
            let formattedDate = "";
            let fullFormattedDate = "";
            if (row.expense_time) {
              try {
                const d = new Date(row.expense_time);
                const day = d.getDate();
                const months = [
                  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
                ];
                const month = months[d.getMonth()];
                const yr = String(d.getFullYear()).slice(-2);
                formattedDate = `${day} ${month} ${yr}`;
                fullFormattedDate = `${day} ${month} ${d.getFullYear()}`;
              } catch (e) {
                formattedDate = row.expense_time;
                fullFormattedDate = row.expense_time;
              }
            }

            return (
              <Paper
                key={row.id}
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: itemSelected
                    ? "primary.main"
                    : isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.08)",
                  borderLeft: `4px solid ${isPaid
                      ? isDark
                        ? "#4ade80"
                        : "#22c55e"
                      : yourImpactType === "lent"
                        ? isDark
                          ? "#4ade80"
                          : "#16a34a"
                        : yourImpactType === "borrowed"
                          ? isDark
                            ? "#f87171"
                            : "#dc2626"
                          : "grey"
                    }`,
                  bgcolor: itemSelected
                    ? isDark
                      ? "rgba(59, 130, 246, 0.08)"
                      : "rgba(59, 130, 246, 0.04)"
                    : isDark
                      ? "rgba(30, 41, 59, 0.55)"
                      : "#ffffff",
                  overflow: "hidden",
                  transition: "background 0.15s ease, border-color 0.15s ease",
                  "&:hover": {
                    bgcolor: isDark
                      ? "rgba(30, 41, 59, 0.85)"
                      : "rgba(0, 0, 0, 0.015)",
                  },
                }}
              >
                {/* Compact Card Row */}
                <Box
                  onClick={() => toggleExpand(row.id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleExpand(row.id);
                    }
                  }}
                  sx={{
                    px: 1.5,
                    py: 1.2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    gap: 1.2,
                  }}
                >
                  {/* Left: Checkbox + Avatar */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={itemSelected}
                      onClick={(e) => handleClick(e, row.id)}
                      sx={{ p: 0.2 }}
                    />

                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: 14,
                        fontWeight: 700,
                        bgcolor: isPaid
                          ? isDark
                            ? "rgba(100, 116, 139, 0.2)"
                            : "rgba(0,0,0,0.06)"
                          : yourImpactType === "lent"
                            ? isDark
                              ? "rgba(34, 197, 94, 0.18)"
                              : "rgba(34, 197, 94, 0.12)"
                            : yourImpactType === "borrowed"
                              ? isDark
                                ? "rgba(239, 68, 68, 0.18)"
                                : "rgba(239, 68, 68, 0.12)"
                              : "action.hover",
                        color: isPaid
                          ? "text.secondary"
                          : yourImpactType === "lent"
                            ? "success.main"
                            : yourImpactType === "borrowed"
                              ? "error.main"
                              : "text.primary",
                        flexShrink: 0,
                      }}
                    >
                      <ReceiptLongIcon sx={{ fontSize: 19 }} />
                    </Avatar>
                  </Box>

                  {/* Middle: Title with directly visible Date & Meta description */}
                  <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.8, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          color: "text.primary",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "0.93rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {row.expense_detail || `Expense #${row.id}`}
                      </Typography>

                      {formattedDate && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            lineHeight: 1.3,
                          }}
                        >
                          {formattedDate}
                        </Typography>
                      )}
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.3,
                        fontSize: "0.74rem",
                        mt: 0.2,
                      }}
                    >
                      {payerName} paid ₹{totalAmount.toFixed(0)}
                      {row.collaborators?.length > 1 ? ` • ${row.collaborators.length} people` : ""}
                    </Typography>
                  </Box>

                  {/* Right: 2-Line Amount & Status block — always shows Total / Effect */}
                  <Box sx={{ flexShrink: 0, textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.3 }}>
                    {/* Line 1: ₹Total / ±Effect + Chevron */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                      {/* Total bill amount — always visible in muted text */}
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: "text.secondary", fontSize: "0.82rem", lineHeight: 1 }}
                      >
                        ₹{totalAmount.toFixed(0)}
                      </Typography>

                      {/* Separator */}
                      <Typography
                        variant="body2"
                        sx={{ color: "divider", fontSize: "0.82rem", lineHeight: 1, mx: 0.1 }}
                      >
                        /
                      </Typography>

                      {/* Effect on you — colored */}
                      {yourImpactType === "lent" && (
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{ color: "success.main", fontSize: "0.95rem", lineHeight: 1 }}
                        >
                          +₹{yourImpactAmount.toFixed(0)}
                        </Typography>
                      )}
                      {yourImpactType === "borrowed" && (
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{ color: "error.main", fontSize: "0.95rem", lineHeight: 1 }}
                        >
                          -₹{yourImpactAmount.toFixed(0)}
                        </Typography>
                      )}
                      {(yourImpactType === "personal" || yourImpactType === "neutral") && (
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: "text.secondary", fontSize: "0.88rem", lineHeight: 1 }}
                        >
                          you paid
                        </Typography>
                      )}

                      <IconButton
                        size="small"
                        sx={{
                          p: 0.1,
                          ml: 0.2,
                          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>

                    {/* Line 2: Impact text (you owe/you lent) + Status Pill */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          color:
                            yourImpactType === "lent"
                              ? "success.main"
                              : yourImpactType === "borrowed"
                                ? "error.main"
                                : "text.secondary",
                          lineHeight: 1,
                        }}
                      >
                        {yourImpactType === "lent"
                          ? "you lent"
                          : yourImpactType === "borrowed"
                            ? "you owe"
                            : "not in split"}
                      </Typography>

                      {isPaid ? (
                        <Chip
                          size="small"
                          label="Settled"
                          sx={{
                            height: 18,
                            fontSize: 9.5,
                            fontWeight: 700,
                            bgcolor: isDark
                              ? "rgba(34, 197, 94, 0.18)"
                              : "success.light",
                            color: isDark ? "#4ade80" : "success.contrastText",
                          }}
                        />
                      ) : isCollaborator && !hasUserAcknowledged && transactionType !== "paid" ? (
                        <Chip
                          size="small"
                          label="Needs OK"
                          color="warning"
                          sx={{
                            height: 18,
                            fontSize: 9.5,
                            fontWeight: 700,
                            px: 0.2,
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label={allAcknowledged ? "Ready" : "Pending"}
                          variant="outlined"
                          sx={{
                            height: 18,
                            fontSize: 9.5,
                            fontWeight: 600,
                            color: "text.secondary",
                            borderColor: "divider",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Detailed Breakdown Drawer (Revealed on Tap) */}
                <Collapse in={isExpanded} timeout="auto">
                  <Divider />
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: isDark
                        ? "rgba(0, 0, 0, 0.2)"
                        : "rgba(0, 0, 0, 0.02)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        display: "block",
                        mb: 1,
                        fontSize: "0.72rem",
                      }}
                    >
                      Split Breakdown ({row.collaborators?.length || 0} participants)
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                      {row.collaborators
                        ?.sort((a, b) => a.name.localeCompare(b.name))
                        .map((collab) => {
                          const isUser = String(collab.id) === String(session?.id);
                          const isCollabPayer = String(collab.id) === String(row.user_id);
                          const isApproved = collab.approved === "true";

                          return (
                            <Box
                              key={collab.id + row.id}
                              sx={{
                                p: 0.8,
                                px: 1.2,
                                borderRadius: 1.8,
                                bgcolor: isDark
                                  ? "rgba(255,255,255,0.04)"
                                  : "background.paper",
                                border: "1px solid",
                                borderColor: isUser
                                  ? "primary.main"
                                  : "divider",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    width: 26,
                                    height: 26,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    bgcolor: isApproved
                                      ? "success.light"
                                      : "warning.light",
                                    color: isApproved
                                      ? "success.contrastText"
                                      : "warning.contrastText",
                                    flexShrink: 0,
                                  }}
                                >
                                  {collab.name?.charAt(0)?.toUpperCase()}
                                </Avatar>
                                <Typography
                                  variant="body2"
                                  fontWeight={isUser ? 700 : 500}
                                  sx={{
                                    fontSize: "0.82rem",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {toTitleCase(collab.name)} {isUser && "(You)"}
                                </Typography>
                                {isCollabPayer && (
                                  <Chip
                                    size="small"
                                    label="Payer"
                                    sx={{
                                      height: 18,
                                      fontSize: 9,
                                      fontWeight: 700,
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.2,
                                  flexShrink: 0,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{
                                    fontSize: "0.85rem",
                                    minWidth: 64,
                                    textAlign: "right",
                                  }}
                                >
                                  ₹{isCollabPayer
                                    ? computedEachShare.toFixed(2)
                                    : parseFloat(collab.shared_amount).toFixed(2)}
                                </Typography>

                                <Box sx={{ width: 100, display: "flex", justifyContent: "flex-end" }}>
                                  {isUser && transactionType !== "paid" && changestatus ? (
                                    <Button
                                      size="small"
                                      variant={isApproved ? "outlined" : "contained"}
                                      color={isApproved ? "success" : "warning"}
                                      startIcon={
                                        isApproved ? (
                                          <DoneAllIcon sx={{ fontSize: "14px !important" }} />
                                        ) : null
                                      }
                                      onClick={() =>
                                        changestatus(row.id, collab.id, collab.approved)
                                      }
                                      sx={{
                                        height: 26,
                                        fontSize: 10.5,
                                        fontWeight: 700,
                                        textTransform: "none",
                                        px: 1,
                                        width: "100%",
                                        borderRadius: 1.5,
                                      }}
                                    >
                                      {isApproved ? "Approved" : "Acknowledge"}
                                    </Button>
                                  ) : (
                                    <Chip
                                      size="small"
                                      icon={
                                        isApproved ? (
                                          <DoneAllIcon sx={{ fontSize: "13px !important" }} />
                                        ) : undefined
                                      }
                                      label={isApproved ? "Approved" : "Pending"}
                                      color={isApproved ? "success" : "default"}
                                      variant={isApproved ? "filled" : "outlined"}
                                      sx={{
                                        height: 26,
                                        fontSize: 10.5,
                                        fontWeight: 600,
                                        width: "100%",
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                    </Box>

                    {/* Actions Bar */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 1.5,
                        pt: 1,
                        borderTop: "1px dashed",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        ID #{row.id} {(fullFormattedDate || formattedDate) && `• ${fullFormattedDate || formattedDate}`} {!isPayer && `• Paid by ${payerName}`}
                      </Typography>

                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        {isPayer && !isPaid && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => {
                              if (!allAcknowledged) {
                                error("Not all collaborators have acknowledged yet.");
                                return;
                              }
                              setConfirmDialog({
                                open: true,
                                title: "Confirm Settle Transaction",
                                content: `Mark "${row.expense_detail || `Transaction #${row.id}`
                                  }" as fully settled?`,
                                confirmText: "Mark Settled",
                                confirmColor: "success",
                                onConfirm: () => {
                                  makePaid(row.id);
                                  setConfirmDialog((prev) => ({
                                    ...prev,
                                    open: false,
                                  }));
                                },
                              });
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: 11,
                              fontWeight: 600,
                              height: 26,
                              borderRadius: 1.5,
                            }}
                          >
                            Mark Settled
                          </Button>
                        )}

                        {isPayer && transactionType !== "paid" && deleteTransactions && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setConfirmDialog({
                                open: true,
                                title: "Confirm Delete Transaction",
                                content: `Are you sure you want to delete "${row.expense_detail || `Transaction #${row.id}`
                                  }"?`,
                                confirmText: "Delete",
                                confirmColor: "error",
                                onConfirm: () => {
                                  deleteTransactions(row.id);
                                  setConfirmDialog((prev) => ({
                                    ...prev,
                                    open: false,
                                  }));
                                },
                              });
                            }}
                            sx={{ p: 0.4 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* ===== PAGINATION ===== */}
      {props.transaction?.length > rowsPerPage && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={props.transaction.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ mt: 1 }}
        />
      )}

      {/* ===== REPORT DIALOG ===== */}
      <Report
        transactions={
          props?.transaction?.filter((tr) => selected.includes(tr.id)) ?? []
        }
        open={open}
        setOpen={setOpen}
        userId={session?.id}
      />

      {/* ===== CONFIRM DIALOG ===== */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        title={confirmDialog.title}
        content={confirmDialog.content}
        confirmText={confirmDialog.confirmText}
        confirmColor={confirmDialog.confirmColor}
        onConfirm={confirmDialog.onConfirm}
      />
    </Box>
  );
};

export default DI(MobileViewOrders);
